import { Box, Typography } from '@mui/material';
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import ReportChip from '../component/reportFilter';
import { IconX } from '@tabler/icons-react';
import { getFirstLevelProjects, getFirstLevelIdSet, resolveDisplayableFirstLevelId, createRootProjectMatcher } from './projectHierarchy';
import { AppConfig } from 'AppConfig';
import { hasPermission } from 'utils/common';
import { EXCLUSIVE_FILTER_ID, EXCLUSIVE_FILTER_NAME, UNCATEGORIZED_FILTER_ID, UNCATEGORIZED_FILTER_NAME } from './reportFilterConstants';


const ReportFilters = ({ isMobile = false, onSelectionChange, resetSelectionTrigger }) => {
  const customs = useSelector((state) => state.fo.customs); // report representation in frontoffice
  const exclusive = useSelector((state) => state.fo.exclusive);
  const permissions = useSelector((state) => state.user.permissions);
  const workbooks = useSelector((state) => state.customReport.workbooks); // report representation in tb server
  const projects = useSelector((state) => state.customReport.projects); // project representation in tb server
  const workbooksLoading = useSelector((state) => state.customReport.workbooksLoading);
  const [selectedIds, setSelectedIds] = useState([]);
  const canViewExclusive = hasPermission(AppConfig.PERMISSION.FO_EXCLUSIVE_REPORTS, permissions);
  // Dedupe by reptSource to match the reports actually rendered on this tab.
  const exclusiveCount = Array.isArray(exclusive) ? _.uniqBy(exclusive, 'reptSource').length : 0;
  // Like the project chips, the Exclusive chip renders at zero; only the permission gates it.
  const showExclusiveChip = canViewExclusive;

  const firstLevelProjects = useMemo(() => getFirstLevelProjects(projects), [projects]);
  const firstLevelIdSet = useMemo(() => getFirstLevelIdSet(projects), [projects]);

  // reports filed directly under the "Custom Reports" root, i.e. not in any subfolder
  const isRootProject = useMemo(() => createRootProjectMatcher(firstLevelProjects), [firstLevelProjects]);

  const { totals, uncategorizedCount } = useMemo(() => {
    if (workbooksLoading || !Array.isArray(customs) || !Array.isArray(workbooks)) {
      return { totals: {}, uncategorizedCount: 0 };
    }
    const customReptSources = new Set(customs.map((report) => String(report.reptSource)));
    const workbookReptSources = new Set(workbooks.map((workbook) => String(workbook.reptSource)));
    const perProject = new Map();
    const uncategorized = new Set(
      [...customReptSources].filter((reptSource) => !workbookReptSources.has(reptSource))
    );
    workbooks.forEach((wb) => {
      if (!customReptSources.has(String(wb.reptSource))) return;
      if (isRootProject(wb.projectId)) {
        uncategorized.add(String(wb.reptSource));
        return;
      }
      const projectKey = resolveDisplayableFirstLevelId(firstLevelIdSet, wb.projectId);
      if (projectKey == null) return;
      if (!perProject.has(projectKey)) perProject.set(projectKey, new Set());
      perProject.get(projectKey).add(String(wb.reptSource));
    });
    const totals = firstLevelProjects.reduce((acc, project) => {
      acc[project.id] = perProject.get(String(project.id))?.size ?? 0;
      return acc;
    }, {});
    return { totals, uncategorizedCount: uncategorized.size };
  }, [customs, workbooks, workbooksLoading, firstLevelProjects, firstLevelIdSet, isRootProject]);

  // unlike the Exclusive chip, Uncategorized only shows when there is at least one such report,
  // and it's hidden when it would be the only chip so all its reports show without a filter.
  const showUncategorizedChip =
    uncategorizedCount > 0 && (firstLevelProjects.length > 0 || showExclusiveChip);

  useEffect(() => {
    setSelectedIds([]);
  }, [resetSelectionTrigger]);

  useEffect(() => {
    if (!showExclusiveChip) {
      setSelectedIds((previousIds) => previousIds.filter((id) => id !== EXCLUSIVE_FILTER_ID));
    }
  }, [showExclusiveChip]);

  useEffect(() => {
    if (!showUncategorizedChip) {
      setSelectedIds((previousIds) => previousIds.filter((id) => id !== UNCATEGORIZED_FILTER_ID));
    }
  }, [showUncategorizedChip]);

  useEffect(() => {
    if (typeof onSelectionChange === 'function') {
      const selected = firstLevelProjects.filter((p) => selectedIds.includes(p.id));
      if (showExclusiveChip && selectedIds.includes(EXCLUSIVE_FILTER_ID)) {
        selected.push({ id: EXCLUSIVE_FILTER_ID, name: EXCLUSIVE_FILTER_NAME });
      }
      if (showUncategorizedChip && selectedIds.includes(UNCATEGORIZED_FILTER_ID)) {
        selected.push({ id: UNCATEGORIZED_FILTER_ID, name: UNCATEGORIZED_FILTER_NAME });
      }
      onSelectionChange(selected);
    }
  }, [selectedIds, firstLevelProjects, showExclusiveChip, showUncategorizedChip, onSelectionChange]);

  const toggleProject = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px', width: '100%' }}>
      {showExclusiveChip && (
        <ReportChip
          item={{ name: EXCLUSIVE_FILTER_NAME }}
          total={exclusiveCount}
          selected={selectedIds.includes(EXCLUSIVE_FILTER_ID)}
          onToggle={() => toggleProject(EXCLUSIVE_FILTER_ID)}
          isMobile={isMobile}
          chipWidth={isMobile ? '100%' : 'auto'}
        />
      )}
      {firstLevelProjects.map((item) => {
          const isSelected = selectedIds.includes(item.id);
          return (
            <ReportChip
              key={item.id}
              item={{ name: item.name }}
              total={totals[item.id] ?? 0}
              selected={isSelected}
              onToggle={() => toggleProject(item.id)}
              isMobile={isMobile}
              chipWidth={isMobile ? '100%' : 'auto'}
            />
          );
        })}        {showUncategorizedChip && (
          <ReportChip
            item={{ name: UNCATEGORIZED_FILTER_NAME }}
            total={uncategorizedCount}
            selected={selectedIds.includes(UNCATEGORIZED_FILTER_ID)}
            onToggle={() => toggleProject(UNCATEGORIZED_FILTER_ID)}
            isMobile={isMobile}
            chipWidth={isMobile ? '100%' : 'auto'}
          />
        )}        {selectedIds.length > 0 && (
          <Box
            component="button"
            type="button"
            onClick={() => setSelectedIds([])}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              cursor: 'pointer',
              color: '#2F54A5',
              marginTop: '5px',
              height: '24px',
              border: 0,
              padding: 0,
              background: 'transparent',
              '&:hover': { color: '#068DF1' }
            }}
          >
            <IconX
              height={16} 
              width={16} 
              style={{cursor:'pointer'}} 
            />
            <Typography
              sx={{ 
                cursor: 'pointer',
                fontFamily: 'Noto Sans', 
                fontSize: '14px', 
                fontStyle: 'normal', 
                fontWeight: 600, 
                height: '24px',
                lineHeight: '150%',
                width: '57px', 
                letterSpacing: '0',
                marginTop: '2px'
              }}>
              Clear All
            </Typography>
          </Box>
        )}
    </Box>
  );
};
export default ReportFilters;
